/* eslint-disable @typescript-eslint/no-explicit-any */
import { createBrowserClient } from '@supabase/ssr'
import type { Database } from './database.types'
import { createMockSupabaseClient } from './mock-data'

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || 'https://inspecdoor-db.frankrath.com'
// Service-Role-Key für Demo-Zugriff
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJzdXBhYmFzZSIsImlhdCI6MTc1MDE5NDY2MCwiZXhwIjo0OTA1ODY4MjYwLCJyb2xlIjoiYW5vbiJ9.rS8cFBuD4TsL9P4Etfn-Dh4HAkYSJ8etgDRn0V-ioPo'

// ✅ SIMPLE MODULE SINGLETON - Keine globalen Variablen
let clientInstance: any = null

function getSupabaseClient() {
  // Server-Side: Immer neue Instanz (SSR-konform)
  if (typeof window === 'undefined') {
    if (!supabaseUrl || !supabaseAnonKey) {
      return createMockSupabaseClient()
    }
    return createBrowserClient<Database>(supabaseUrl, supabaseAnonKey)
  }

  // Client-Side: Echte Singleton mit lazy initialization
  if (!clientInstance) {
    if (!supabaseUrl || !supabaseAnonKey) {
      console.log('Using mock data for demo (Supabase not configured)')
      clientInstance = createMockSupabaseClient()
    } else {
      console.log('✅ Creating Supabase client')
      clientInstance = createBrowserClient<Database>(supabaseUrl, supabaseAnonKey)
    }
  }

  return clientInstance
}

export const supabase = getSupabaseClient()

export function createClientSupabaseClient() {
  return getSupabaseClient()
}

// 🚨 BERECHTIGUNGS-INTERFACE (MVP-Vorbereitung für Auth-System)
export interface UserPermissions {
  canDelete: boolean;
  role: 'admin' | 'monteur' | 'sachbearbeiter';
  userId?: string;
}

// Mock-Berechtigungen für MVP (später durch echtes Auth-System ersetzen)
export const getMockPermissions = (): UserPermissions => ({
  canDelete: true, // MVP: Alle können löschen
  role: 'admin',
  userId: 'mock-user'
});

// 🗑️ DELETE-VALIDATION INTERFACE
export interface DeleteValidationResult {
  canDelete: boolean;
  blockedBy?: string;
  dependentCount?: number;
  message: string;
}

// Helper functions for common operations
export const supabaseHelpers = {
  async getCustomers() {
    const { data, error } = await getSupabaseClient()
      .from('customers')
      .select(`
        *,
        delivery_locations (
          *,
          doors (
            id,
            door_number,
            status,
            next_inspection_date
          )
        )
      `)
      .eq('status', true)
      .order('customer_number')
    
    return { data, error }
  },

  async getDeliveryLocations(customerId?: string) {
    let query = supabase
      .from('delivery_locations')
      .select(`
        *,
        customers (*),
        doors (
          id,
          door_number,
          status,
          next_inspection_date
        )
      `)
      .eq('status', true)

    if (customerId) {
      query = query.eq('customer_id', customerId)
    }

    const { data, error } = await query.order('location_number')
    return { data, error }
  },

  async getDoors(deliveryLocationId?: string) {
    let query = supabase
      .from('doors')
      .select(`
        *,
        delivery_locations (*),
        inspections (
          id,
          inspection_date,
          inspection_successful
        ),
        repairs (
          id,
          status,
          cost
        )
      `)

    if (deliveryLocationId) {
      query = query.eq('delivery_location_id', deliveryLocationId)
    }

    const { data, error } = await query.order('door_number')
    return { data, error }
  },

  async getUpcomingInspections(days = 30) {
    const targetDate = new Date()
    targetDate.setDate(targetDate.getDate() + days)

    const { data, error } = await getSupabaseClient()
      .from('doors')
      .select(`
        *,
        delivery_locations (
          *,
          customers (*)
        )
      `)
      .lte('next_inspection_date', targetDate.toISOString().split('T')[0])
      .order('next_inspection_date')

    return { data, error }
  },

  // 🗑️ DELETE-VALIDATION FUNCTIONS (Hierarchie-Aware)
  
  async validateCustomerDeletion(customerId: string): Promise<DeleteValidationResult> {
    // Prüfe auf abhängige Lieferorte
    const { data: locations, error } = await getSupabaseClient()
      .from('delivery_locations')
      .select('id, location_name')
      .eq('customer_id', customerId)
      .eq('status', true)

    if (error) {
      return {
        canDelete: false,
        message: 'Fehler beim Prüfen der Abhängigkeiten'
      }
    }

    if (locations && locations.length > 0) {
      return {
        canDelete: false,
        blockedBy: 'delivery_locations',
        dependentCount: locations.length,
        message: `Kunde kann nicht gelöscht werden. ${locations.length} aktive Lieferorte vorhanden.`
      }
    }

    return {
      canDelete: true,
      message: 'Kunde kann sicher gelöscht werden.'
    }
  },

  async validateLocationDeletion(locationId: string): Promise<DeleteValidationResult> {
    // Prüfe auf abhängige Standorte
    const { data: sites, error: sitesError } = await getSupabaseClient()
      .from('sites')
      .select('id, site_name')
      .eq('delivery_location_id', locationId)
      .eq('status', true)

    if (sitesError) {
      return {
        canDelete: false,
        message: 'Fehler beim Prüfen der Standorte'
      }
    }

    if (sites && sites.length > 0) {
      return {
        canDelete: false,
        blockedBy: 'sites',
        dependentCount: sites.length,
        message: `Lieferort kann nicht gelöscht werden. ${sites.length} aktive Standorte vorhanden.`
      }
    }

    // Prüfe auf abhängige Türen (falls direkt zugeordnet)
    const { data: doors, error: doorsError } = await getSupabaseClient()
      .from('doors')
      .select('id, door_number')
      .eq('delivery_location_id', locationId)
      .is('site_id', null) // Türen direkt am Lieferort

    if (doorsError) {
      return {
        canDelete: false,
        message: 'Fehler beim Prüfen der Türen'
      }
    }

    if (doors && doors.length > 0) {
      return {
        canDelete: false,
        blockedBy: 'doors',
        dependentCount: doors.length,
        message: `Lieferort kann nicht gelöscht werden. ${doors.length} direkt zugeordnete Türen vorhanden.`
      }
    }

    return {
      canDelete: true,
      message: 'Lieferort kann sicher gelöscht werden.'
    }
  },

  async validateSiteDeletion(siteId: string): Promise<DeleteValidationResult> {
    // Prüfe auf abhängige Türen
    const { data: doors, error } = await getSupabaseClient()
      .from('doors')
      .select('id, door_number')
      .eq('site_id', siteId)

    if (error) {
      return {
        canDelete: false,
        message: 'Fehler beim Prüfen der Türen'
      }
    }

    if (doors && doors.length > 0) {
      return {
        canDelete: false,
        blockedBy: 'doors',
        dependentCount: doors.length,
        message: `Standort kann nicht gelöscht werden. ${doors.length} zugeordnete Türen vorhanden.`
      }
    }

    return {
      canDelete: true,
      message: 'Standort kann sicher gelöscht werden.'
    }
  },

  async validateDoorDeletion(doorId: string): Promise<DeleteValidationResult> {
    // Prüfe auf aktive Prüfungen
    const { data: inspections, error: inspError } = await getSupabaseClient()
      .from('inspections')
      .select('id, inspection_date')
      .eq('door_id', doorId)

    if (inspError) {
      return {
        canDelete: false,
        message: 'Fehler beim Prüfen der Prüfungen'
      }
    }

    // Prüfe auf offene Reparaturen
    const { data: repairs, error: repError } = await getSupabaseClient()
      .from('repairs')
      .select('id, status')
      .eq('door_id', doorId)
      .neq('status', 'completed')

    if (repError) {
      return {
        canDelete: false,
        message: 'Fehler beim Prüfen der Reparaturen'
      }
    }

    const openRepairs = repairs?.filter((r: any) => r.status === 'open' || r.status === 'in_progress') || []

    if (openRepairs.length > 0) {
      return {
        canDelete: false,
        blockedBy: 'repairs',
        dependentCount: openRepairs.length,
        message: `Tür kann nicht gelöscht werden. ${openRepairs.length} offene Reparaturen vorhanden.`
      }
    }

    // Warnung bei vorhandenen Prüfungen (erlaubt aber trotzdem löschen)
    const inspectionCount = inspections?.length || 0
    if (inspectionCount > 0) {
      return {
        canDelete: true,
        message: `Tür kann gelöscht werden. WARNUNG: ${inspectionCount} Prüfprotokolle werden mit gelöscht.`
      }
    }

    return {
      canDelete: true,
      message: 'Tür kann sicher gelöscht werden.'
    }
  },

  // 🗑️ DELETE-EXECUTION FUNCTIONS (mit Berechtigungsprüfung)

  async deleteCustomer(customerId: string, userPermissions: UserPermissions = getMockPermissions()) {
    // Berechtigungsprüfung
    if (!userPermissions.canDelete) {
      return {
        success: false,
        error: 'Keine Berechtigung zum Löschen von Kunden.'
      }
    }

    // Hierarchie-Validierung
    const validation = await this.validateCustomerDeletion(customerId)
    if (!validation.canDelete) {
      return {
        success: false,
        error: validation.message
      }
    }

    try {
      // Soft-Delete: status auf false setzen
      const { data, error } = await getSupabaseClient()
        .from('customers')
        .update({ 
          status: false,
          deleted_at: new Date().toISOString(),
          deleted_by: userPermissions.userId || 'unknown'
        })
        .eq('id', customerId)
        .select()

      if (error) throw error

      return {
        success: true,
        data,
        message: 'Kunde erfolgreich gelöscht.'
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Fehler beim Löschen des Kunden.'
      }
    }
  },

  async deleteLocation(locationId: string, userPermissions: UserPermissions = getMockPermissions()) {
    if (!userPermissions.canDelete) {
      return {
        success: false,
        error: 'Keine Berechtigung zum Löschen von Lieferorten.'
      }
    }

    const validation = await this.validateLocationDeletion(locationId)
    if (!validation.canDelete) {
      return {
        success: false,
        error: validation.message
      }
    }

    try {
      const { data, error } = await getSupabaseClient()
        .from('delivery_locations')
        .update({ 
          status: false,
          deleted_at: new Date().toISOString(),
          deleted_by: userPermissions.userId || 'unknown'
        })
        .eq('id', locationId)
        .select()

      if (error) throw error

      return {
        success: true,
        data,
        message: 'Lieferort erfolgreich gelöscht.'
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Fehler beim Löschen des Lieferorts.'
      }
    }
  },

  async deleteSite(siteId: string, userPermissions: UserPermissions = getMockPermissions()) {
    if (!userPermissions.canDelete) {
      return {
        success: false,
        error: 'Keine Berechtigung zum Löschen von Standorten.'
      }
    }

    const validation = await this.validateSiteDeletion(siteId)
    if (!validation.canDelete) {
      return {
        success: false,
        error: validation.message
      }
    }

    try {
      const { data, error } = await getSupabaseClient()
        .from('sites')
        .update({ 
          status: false,
          deleted_at: new Date().toISOString(),
          deleted_by: userPermissions.userId || 'unknown'
        })
        .eq('id', siteId)
        .select()

      if (error) throw error

      return {
        success: true,
        data,
        message: 'Standort erfolgreich gelöscht.'
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Fehler beim Löschen des Standorts.'
      }
    }
  },

  async deleteDoor(doorId: string, userPermissions: UserPermissions = getMockPermissions()) {
    if (!userPermissions.canDelete) {
      return {
        success: false,
        error: 'Keine Berechtigung zum Löschen von Türen.'
      }
    }

    const validation = await this.validateDoorDeletion(doorId)
    if (!validation.canDelete) {
      return {
        success: false,
        error: validation.message
      }
    }

    try {
      // Für Türen: Echtes Löschen (da weniger kritisch für Hierarchie)
      const { data, error } = await getSupabaseClient()
        .from('doors')
        .delete()
        .eq('id', doorId)
        .select()

      if (error) throw error

      return {
        success: true,
        data,
        message: 'Tür erfolgreich gelöscht.'
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Fehler beim Löschen der Tür.'
      }
    }
  },

  // 🔄 RESTORE FUNCTIONS (für MVP später erweiterbar)
  
  async restoreCustomer(customerId: string, userPermissions: UserPermissions = getMockPermissions()) {
    if (!userPermissions.canDelete) { // Gleiche Berechtigung wie Delete
      return {
        success: false,
        error: 'Keine Berechtigung zum Wiederherstellen von Kunden.'
      }
    }

    try {
      const { data, error } = await getSupabaseClient()
        .from('customers')
        .update({ 
          status: true,
          deleted_at: null,
          deleted_by: null
        })
        .eq('id', customerId)
        .select()

      if (error) throw error

      return {
        success: true,
        data,
        message: 'Kunde erfolgreich wiederhergestellt.'
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Fehler beim Wiederherstellen des Kunden.'
      }
    }
  }
}
